import type { ProxyClientService, TableProxyTarget } from './index';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { AuthStatus, ProxyClient, ProxyNgClient } from '@thinkalpha/table-client';
import type { ApiAuth, ConnectionStatus, ObjectDefinitionResult, RawClient } from '@thinkalpha/table-client';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { optional } from 'inversify';
import { Observable } from 'rxjs';
import { inject, injectable } from 'src/features/ioc';
import { appConfig } from 'src/lib/config';
import { useNextGenProxy } from 'src/lib/featureFlags/flags/useNextGenProxy';
import type { IsomorphicBindings, ReactBindings } from 'src/types/bindings';

@injectable()
export class ProxyClientServiceImpl implements ProxyClientService {
    #client: ProxyNgClient | ProxyClient | undefined;
    #log: IsomorphicBindings['Logger']; // local logger with context
    /** Service-lifetime subscriptions (the `use-nextgen-proxy` flag watcher). */
    #serviceDisposables = new DisposableStack();
    /**
     * Subscriptions wired to the current `#client`. Held in a `DisposableBox` because the client is a
     * rotating value: each transport swap replaces the box's contents via `next`, which disposes the
     * previous client's subscriptions automatically. Registered with `#serviceDisposables` so it is
     * torn down with the service.
     */
    #clientDisposables = this.#serviceDisposables.use(new DisposableBox<DisposableStack>());
    /** Auth state captured before the client is built, (re-)applied to each client that gets created. */
    #accessToken: string | ApiAuth | undefined;
    #lastRejectedAccessToken: string | undefined;
    /**
     * Whether table requests opt into superuser access. The proxy applies superuser access to any
     * superuser token unless the client opts out, so this follows the user's superuser-mode toggle;
     * the proxy still decides whether the token is entitled to it.
     */
    #superuser = signal(false);
    /** The `use-nextgen-proxy` value the current `#client` was built from, used to detect real transport changes. */
    #currentUseNextGen: boolean | undefined;
    #generation = 0;
    #target!: Signal<TableProxyTarget>;
    /** Status adapters are installed only after the existing client lifecycle creates a raw client. */
    #connectionStatusByClient = new WeakMap<RawClient, Signal<ConnectionStatus | undefined>>();
    #connectionStatus = computed(() => this.#connectionStatusByClient.get(this.target.value.client)?.value);

    get target(): ReadonlySignal<TableProxyTarget> {
        return this.#target;
    }

    get connectionStatus(): ReadonlySignal<ConnectionStatus | undefined> {
        return this.#connectionStatus;
    }

    get superuser(): ReadonlySignal<boolean> {
        return this.#superuser;
    }

    constructor(
        @inject('Logger') logger: IsomorphicBindings['Logger'],
        // `FeatureFlagService` is only bound in the main `ReactContainer`; the Electron main and
        // worker containers build this service without it, so it is optional. Those containers fall
        // back to the flag's default and enforce the real transport imperatively via
        // `setUseNextGenProxy` (relayed from the main thread), analogous to `setAccessToken`.
        @inject('FeatureFlagService')
        @optional()
        private featureFlagService?: ReactBindings['FeatureFlagService'] | undefined,
        @inject('SessionService')
        @optional()
        private sessionService?: IsomorphicBindings['SessionService'] | undefined,
        // Bound in the main `ReactContainer` and the Electron main container. Workers build this
        // service without it and relay the superuser toggle imperatively via `setSuperuserFlag`.
        @inject('CurrentUserModel')
        @optional()
        currentUserModel?: ReactBindings['CurrentUserModel'] | undefined,
    ) {
        this.#log = logger.getSubLogger({ name: 'table:authentication' });
        // Watch the toggle before building the client so the first login already carries it.
        if (currentUserModel) {
            this.#serviceDisposables.defer(
                effect(() => {
                    this.#applySuperuser(currentUserModel.isSuperUserModeEnabled.value);
                }),
            );
        }
        this.#initClient();
    }

    /**
     * Builds the client eagerly from the current `use-nextgen-proxy` flag value and keeps it in sync
     * with the flag afterwards.
     *
     * The transport (next-gen flatbuffer WebSocket vs. legacy socket.io/REST) is **swappable**: the
     * flag watcher builds the initial client on its first (synchronous) run and, whenever the flag
     * toggles, `#rebuildClient` builds a fresh client, re-applies the captured auth state, publishes a
     * new transport target, and scraps the previous one. Consumers that must survive the swap react
     * to the target signal rather than capturing the raw client by reference.
     *
     * When `FeatureFlagService` is unavailable (Electron main / worker containers) there is no signal
     * to watch, so the client is built once from the flag's default value; callers can then enforce
     * the transport imperatively via `setUseNextGenProxy` (e.g. relayed from the main thread).
     */
    #initClient(): void {
        if (!this.featureFlagService) {
            this.#buildClient(useNextGenProxy.defaultValue);
            return;
        }

        const flagSignal = this.featureFlagService.getSignal(useNextGenProxy);
        this.#serviceDisposables.defer(
            effect(() => {
                this.#applyUseNextGen(flagSignal.value);
            }),
        );
    }

    /**
     * Builds the client on first use or swaps the transport when the requested value differs from
     * the one the current client was built from. Shared by the flag watcher and the imperative
     * `setUseNextGenProxy` setter.
     */
    #applyUseNextGen(useNextGen: boolean): void {
        if (!this.#client) {
            this.#buildClient(useNextGen);
        } else if (useNextGen !== this.#currentUseNextGen) {
            this.#rebuildClient(useNextGen);
        }
    }

    /**
     * Builds `#client` for the given flag value, wires up its subscriptions, and publishes a new
     * transport target. Called for the initial build and again for every transport swap.
     */
    #buildClient(useNextGen: boolean): void {
        const clientDisposables = new DisposableStack();

        // Use the next-gen flatbuffer WebSocket proxy when the flag is on, otherwise fall back to
        // the legacy socket.io/REST proxy.
        const client = useNextGen
            ? new ProxyNgClient(appConfig.wsNgTableProxy, undefined, this.#superuser.peek(), this.#log)
            : new ProxyClient(appConfig.tableProxy, undefined, this.#superuser.peek(), this.#log);

        // Apply the access token captured from earlier `setAccessToken` calls so the freshly built
        // client logs in immediately. This runs for the initial client and for every replacement
        // built after a flag change.
        client.token = this.#accessToken;

        // Adapt status into a signal for this already-created client before publishing it as the
        // active target. This does not participate in client creation or cause the inactive
        // transport to be constructed.
        const connectionStatus = signal<ConnectionStatus | undefined>(undefined);
        this.#connectionStatusByClient.set(client, connectionStatus);
        {
            const subscription = client.connectionStatus$.subscribe((status) => {
                connectionStatus.value = status;
            });
            clientDisposables.defer(() => subscription.unsubscribe());
        }

        {
            const subscription = client.authStatus$.subscribe((status) => {
                if (status === AuthStatus.badToken) {
                    this.#log.warn({ message: 'table-ws has rejected the user token' });
                    const rejectedToken = typeof this.#accessToken === 'string' ? this.#accessToken : undefined;
                    if (rejectedToken && rejectedToken !== this.#lastRejectedAccessToken) {
                        this.#lastRejectedAccessToken = rejectedToken;
                        void this.sessionService?.refresh(rejectedToken).catch((error) => {
                            this.#log.error({
                                message: 'Failed to report table-ws token rejection',
                                error,
                            });
                        });
                    }
                }
            });
            clientDisposables.defer(() => subscription.unsubscribe());
        }

        {
            const subscription = client.objectDefinitionResult$.subscribe((res) => {
                if (!res.success) {
                    this.#log.error({ message: res.comment });
                }
            });
            clientDisposables.defer(() => subscription.unsubscribe());
        }

        this.#client = client;
        this.#clientDisposables.next(clientDisposables);
        this.#currentUseNextGen = useNextGen;
        const target: TableProxyTarget = {
            client,
            generation: this.#generation++,
            kind: useNextGen ? 'nextgen' : 'legacy',
        };
        if (this.#target) {
            this.#target.value = target;
        } else {
            this.#target = signal(target);
        }
    }

    /**
     * Swaps the transport after a `use-nextgen-proxy` flag change: builds the replacement client
     * (which re-applies the captured access token and superuser flag and publishes a target), then
     * scraps the previous client by tearing down its subscriptions and closing its socket.
     */
    #rebuildClient(useNextGen: boolean): void {
        const previousClient = this.#client;

        this.#log.info({
            message: 'use-nextgen-proxy changed; rebuilding the table client',
            useNextGen,
        });

        // Building the replacement swaps the `#clientDisposables` box, which disposes the previous
        // client's subscriptions automatically.
        this.#buildClient(useNextGen);

        // Scrap the previous client: close its transport so its WebSocket does not leak.
        if (previousClient) {
            this.#closeClient(previousClient);
        }
    }

    /**
     * Closes a scrapped client so it does not leak its transport.
     *
     * Both `ProxyNgClient` and the legacy `ProxyClient` expose `close()`, which shuts their transport
     * and releases the subscriptions they opened internally. Without this, every transport swap —
     * including the initial legacy→next-gen swap that happens once the flag resolves on load — would
     * leave the previous client's WebSocket connected forever.
     */
    #closeClient(client: ProxyNgClient | ProxyClient): void {
        client.close();
    }

    setAccessToken(accessToken: string | ApiAuth | null | undefined): void {
        if (TRACE) {
            this.#log.trace({ message: 'About to log in using token', accessToken });
        }
        // Stash the token so it can be re-applied to any client the flag watcher rebuilds, and apply
        // it to the current client immediately.
        const normalizedAccessToken = accessToken ?? undefined;
        this.#accessToken = normalizedAccessToken;
        if (normalizedAccessToken === undefined) {
            this.#lastRejectedAccessToken = undefined;
        }
        if (this.#client) {
            this.#client.token = normalizedAccessToken;
        }
    }

    setSuperuserFlag(flag: boolean): void {
        this.#applySuperuser(flag);
    }

    /**
     * Stashes the flag for clients built later and applies it to the live client, which logs in
     * again on its current connection. Unchanged values are skipped because the legacy client logs
     * in again on every write. The signal is published only after the live client has sent its new
     * login, so a subscription made in reaction to it is ordered behind that login.
     */
    #applySuperuser(flag: boolean): void {
        if (flag === this.#superuser.peek()) {
            return;
        }
        if (TRACE) {
            this.#log.trace({ message: `About to set superuser to ${flag}`, flag });
        }
        if (this.#client) {
            this.#client.useSuperuser = flag;
        }
        this.#superuser.value = flag;
    }

    setUseNextGenProxy(useNextGen: boolean): void {
        if (TRACE) {
            this.#log.trace({ message: 'Enforcing proxy transport from outside', useNextGen });
        }
        // Mirrors what the flag watcher does, for containers without a `FeatureFlagService` (workers)
        // where the value is relayed in from the main thread. Builds the client on first call or
        // swaps the transport when it differs from the current one.
        this.#applyUseNextGen(useNextGen);
    }

    getAuthStatus$(): Observable<AuthStatus | undefined> {
        // Auth status is streaming data, so its public representation remains an Observable. The
        // transport-selection mechanism stays signal-based and only replaces the inner subscription.
        return this.#followTargetStream((client) => client.authStatus$);
    }

    getObjectDefinitionResult$(): Observable<ObjectDefinitionResult> {
        return this.#followTargetStream((client) => client.objectDefinitionResult$);
    }

    #followTargetStream<T>(getStream: (client: RawClient) => Observable<T>): Observable<T> {
        return new Observable((subscriber) => {
            let clientSubscription: { unsubscribe(): void } | undefined;
            const disposeEffect = effect(() => {
                const client = this.target.value.client;
                untracked(() => {
                    clientSubscription?.unsubscribe();
                    clientSubscription = getStream(client).subscribe({
                        next: (value) => subscriber.next(value),
                        error: (error) => subscriber.error(error),
                    });
                });
            });

            return () => {
                disposeEffect();
                clientSubscription?.unsubscribe();
            };
        });
    }

    // TODO: Commenting this is a temporary workaround for a bug in ReactiveInjectable which does not consider if an
    // injectable is "owned" (new instance) or "shared" (singleton instance).
    /*
[Symbol.dispose](): void {
// Disposes the flag watcher and the `#clientDisposables` box (which disposes the current client's
// subscriptions).
this.#serviceDisposables.dispose();
// Close the client's transport so its WebSocket does not leak. `ProxyNgClient` owns a WebSocket
// and the legacy `ProxyClient` owns a socket.io connection; `#closeClient` handles both.
if (this.#client) {
this.#closeClient(this.#client);
}
}
*/
}
